import _ from 'lodash'
import Utils from '@/utils/utils'
import { v4 as uuidv4 } from 'uuid'

import { TaskItemTypes, TaskItemStatusTypes, TaskTypeOrderLinkTypes, TaskTypeInvoicingRuleTypes, InventoryStockTypes } from '@/store/types'

export default {
  methods: {
    /**
     * Generate a name for the task item based on orders
     * If all same contacts, will use that contact name
     * Otherwise if all have same delivery tag, will use that instead
     * Orders must contain order -> contact -> name
     * 
     */
    generateDespatchTaskItemNameForOrders(taskItem, orderList, options = {}) {
      if(!orderList || orderList.length == 0)
        return taskItem.locationAddress

      let taskItemName = orderList[0].contact.name

      // If multiple contacts, display contact count
      let contactCount = _.uniq(orderList.map(order => order.contact.name)).length
      if(contactCount > 1) {
        taskItemName = `${contactCount} Contacts`

        // If all orders are with the same delivery tag, use that instead
        let deliveryTagList = _.uniq(_.compact(orderList.map(order => {

          if(order.deliveryTag && order.deliveryTag.length > 0) {
            if(order.deliveryTag[0].name == '')
              return null

            return order.deliveryTag[0].name
          }

          return null
        })))

        if(deliveryTagList.length == 1)
          taskItemName = deliveryTagList[0]
      }

      // Check for address name lookups
      if(_.isArray(options.addressLookupList)) {
        let matchingAddrLookup = options.addressLookupList.find(addrLookup => {
          return Utils.compareAddressString(addrLookup.value, taskItem.locationAddress)
        })

        if(matchingAddrLookup)
          taskItemName = matchingAddrLookup.description
      }

      return taskItemName
    },


    /**
     * Generate the details/instructions for the task item based on orders
     * Uses delivery instructions from orders. Otherwise empty string
     */
    generateDespatchTaskDetailsForOrders(taskItem, orderList) {
      var deliveryInstrList = _.uniq(_.compact(orderList.map(order => ((order.deliveryInstructions || '') + '').trim())))
      return deliveryInstrList.join(', ')
    },


    /**
     * Calculate the best icon for a task type
     */
    getTaskTypeIcon(taskType) {
      if(taskType.isLocationTrackingEnabled && taskType.isDespatchEnabled)
        return 'fa-truck'

      if(!taskType.isLocationTrackingEnabled && taskType.isDespatchEnabled)
        return 'fa-person-carry'

      return 'fa-calendar-day'
    },


    /**
     * Check if an order item contains trackable units to load / unload
     * Items like services / non-track-tracking items do not have units to load / unload
     */
    isOrderItemTrackableType(orderItem) {
      // Ignore items that have a parent item (eg, sub-items in assemblies/templates)
      if(orderItem.parentOrderItemId != null)
        return false

      // Ignore services
      if(orderItem.stockType == InventoryStockTypes.glassService)
        return false

      // If a general item, only include it if flagged for task completable
      if(orderItem.stockType == InventoryStockTypes.general && !orderItem.stock.isTrackingQuantity)
        return false

      return true
    },


    /**
     * Check if an order item can be included into the runsheet for tasks
     * Items like services / non-task-completable items cannot be included
     */
    isOrderItemRunsheetProcessable(orderItem) {
      // Ignore items that have a parent item (eg, sub-items in assemblies/templates)
      if(orderItem.parentOrderItemId != null)
        return false

      // Ignore services
      if(orderItem.stockType == InventoryStockTypes.glassService)
        return false

      // If a general item, only include it if flagged for task completable
      if(orderItem.stockType == InventoryStockTypes.general && !orderItem.stock.isTaskCompletable)
        return false

      return true
    },


    /**
     * Build the task item list given a list of orders for order despatch based tasks
     * Supports merging with another existing task
     * 
     * Adds in additional properties used by UI task item list
     * 
     * Returns a promise resolved on completion
     */
    generateTaskItemList(taskType, currentTaskItemList, orderIdList, ignoreOrderItemIdList, mergeTaskId = null, addressLookupList) {
      return new Promise(async (resolve, reject) => {
        try {
          var isOrderLink = taskType.orderLinkType == TaskTypeOrderLinkTypes.order
          var isOrderItemLink = taskType.orderLinkType == TaskTypeOrderLinkTypes.orderItem
          var taskItemType = TaskItemTypes.orderDespatch

          var orderList = []
          var mergeDestTask = null

          // If task type does not despatch items, just use a general task item type
          if(taskType.invoiceItemRuleType == TaskTypeInvoicingRuleTypes.none)
            taskItemType = TaskItemTypes.general

          var requestList = []
          if(orderIdList.length > 0) {
            requestList.push(this.$apiService.tasksService.getTaskOrderList(orderIdList))
            if(mergeTaskId) 
              requestList.push(this.$apiService.tasksService.getTask(mergeTaskId))
          }

          if(requestList.length > 0) {
            var results = await Promise.all(requestList)
            orderList = results[0].data
            if(mergeTaskId && requestList.length > 1)
              mergeDestTask = results[1].data
          }
    
          // Clear all non-merged orders / order items
          // Keep list to retain order / non-despatch types
          currentTaskItemList.forEach(taskItem => {
            taskItem.orders = taskItem.orders.filter(order => order.fromMergedTask)
            taskItem.orderItems = taskItem.orderItems.filter(orderItem => orderItem.order.fromMergedTask)
          })

          if(mergeDestTask) {
            // Just copy over the merge task items first...
            mergeDestTask.taskItems.forEach(mergeTaskItem => {
              // Ignore invalid task items
              if(mergeTaskItem.status == TaskItemStatusTypes.completed || mergeTaskItem.status == TaskItemStatusTypes.cancelled)
                return

              let existingTaskItem = currentTaskItemList.find(taskItem => mergeTaskItem.id == taskItem.taskItemId && taskItem.fromMergedTask)
              if(!existingTaskItem) {

                let newMergeTaskItem = {
                  id: uuidv4(),
                  type: mergeTaskItem.type,
                  taskItemId: mergeTaskItem.id,
                  fromMergedTask: true,
                  name: mergeTaskItem.name,
                  details: mergeTaskItem.details,
                  key: ('' + mergeTaskItem.locationAddress).toLowerCase(),
                  isLocationEnabled: mergeTaskItem.isLocationEnabled,
                  locationAddress: mergeTaskItem.locationAddress,
                  estimatedProcessingTime: mergeTaskItem.estimatedProcessingTime,
                  totalOrderItemRequiredQuantity: this.calculateOrderItemListRequiredQuantity(isOrderItemLink ? mergeTaskItem.orderItems : []),
                  expanded: false,
                  orders: isOrderLink ? _.cloneDeep(mergeTaskItem.orders) : [],
                  orderItems: isOrderItemLink ? _.cloneDeep(mergeTaskItem.orderItems) : []
                }

                // Flag merged orders / order items as from the merged task
                newMergeTaskItem.orders.forEach(order => order.fromMergedTask = true)
                newMergeTaskItem.orderItems.forEach(orderItem => orderItem.order.fromMergedTask = true)

                currentTaskItemList.push(newMergeTaskItem)
              }
            })
          }

          // If not merging tasks, remove items with merge task flag set
          if(!mergeDestTask) {
            currentTaskItemList = currentTaskItemList.filter(taskItem => !taskItem.fromMergedTask)
          }

          // Build selected orders into runsheet
          orderList.forEach(order => {
            let curGroup = null
            let groupKey = ''
            let locationAddress = ''

            if(taskType.isLocationTrackingEnabled) {
              locationAddress = Utils.buildAddressString(order.deliveryAddress1, order.deliveryAddress2, order.deliverySuburb, order.deliveryPostcode, order.deliveryState, '', ' ')
              let locationKeyList = _.compact(Object.values(_.pick(order, ['deliveryAddress1', 'deliveryAddress2', 'deliverySuburb', 'deliveryPostcode', 'deliveryState', 'deliveryCountry'])))
              locationKeyList = locationKeyList.map(key => ('' + key).trim())

              groupKey = locationKeyList.join(',').toLowerCase()

              // Attempt to merge with merge task items first
              if(mergeDestTask) {
                curGroup = currentTaskItemList.find(taskItem => {
                  if(!taskItem.fromMergedTask)
                    return false
                  if(taskItem.type != taskItemType)
                    return false
                  return taskItem.key == groupKey || Utils.compareAddressString(taskItem.locationAddress, locationAddress)
                })
              }

              if(!curGroup) {
                curGroup = currentTaskItemList.find(taskItem => {
                  if(taskItem.type != taskItemType)
                    return false
                  return taskItem.key == groupKey || Utils.compareAddressString(taskItem.locationAddress, locationAddress)
                })
              }
            } else {
              // If not location based, just group all orders into the 1st relevant task item
              curGroup = currentTaskItemList.find(taskItem => taskItem.type == taskItemType)
            }

            // Filter out items we can ignore            
            order.orderItems = order.orderItems.filter(orderItem => {
              // Only items with quantities left to despatch
              if(orderItem.despatchedQuantity >= orderItem.quantity)
                return false

              // Only items that can be included in runsheets
              return this.isOrderItemRunsheetProcessable(orderItem)
            })

            // Ignore order items we don't want
            order.orderItems = order.orderItems.filter(orderItem => !ignoreOrderItemIdList.includes(orderItem.id))

            // Skip orders with no order items to despatch
            if(order.orderItems.length == 0 && isOrderItemLink)
              return

            // Convert order items into a format for displaying on UI
            if(isOrderItemLink) {
              order.orderItems.forEach(orderItem => {
                orderItem.order = {
                  id: order.id,
                  contact: order.contact,
                  sequenceNumber: order.sequenceNumber,
                  deliveryTag: order.deliveryTag,
                  deliveryInstructions: order.deliveryInstructions
                }
              })
            }

            if(curGroup) {
              if(isOrderLink) {
                // Avoid duplicates
                if(!curGroup.orders.some(listOrder => listOrder.id == order.id))
                  curGroup.orders.push(order)

              } else if(isOrderItemLink) {
                order.orderItems.forEach(orderItem => {
                  // Avoid duplicates
                  if(!curGroup.orderItems.some(listOrderItem => listOrderItem.id == orderItem.id))
                    curGroup.orderItems.push(orderItem)
                })

                curGroup.totalOrderItemRequiredQuantity = this.calculateOrderItemListRequiredQuantity(curGroup.orderItems)
              }

            } else {
              currentTaskItemList.push({
                id: uuidv4(),
                type: taskItemType,
                name: taskType.name,
                details: '',
                key: groupKey,
                expanded: false,
                estimatedProcessingTime: taskType.defaultTaskTime,
                isLocationEnabled: taskType.isLocationTrackingEnabled,
                locationAddress: locationAddress,
                totalOrderItemRequiredQuantity: this.calculateOrderItemListRequiredQuantity(isOrderItemLink ? order.orderItems : []),
                orders: isOrderLink ? [order] : [],
                orderItems: isOrderItemLink ? order.orderItems : []
              })
            }
          })


          // Remove order despatch task items with no orders / order items
          // Leave other types
          currentTaskItemList = currentTaskItemList.filter(taskItem => {
            if(taskItem.type != TaskItemTypes.orderDespatch)
              return true

            if(isOrderItemLink)
              return taskItem.orderItems.length > 0
            if(isOrderLink)
              return taskItem.orders.length > 0
            return true
          })

          // Generate all name/details info
          currentTaskItemList.forEach(taskItem => {
            if(taskItem.type != TaskItemTypes.orderDespatch)
              return

            if(isOrderItemLink) {
              if(!taskItem.editingName)
                taskItem.name = this.generateDespatchTaskItemNameForOrders(taskItem, taskItem.orderItems.map(oi => oi.order), { addressLookupList: addressLookupList })

              if(!taskItem.editingDetails)
                taskItem.details = this.generateDespatchTaskDetailsForOrders(taskItem, taskItem.orderItems.map(oi => oi.order))

              let unitCount = taskItem.orderItems.reduce((curValue, orderItem) => curValue + (orderItem.quantity - orderItem.despatchedQuantity), 0)

              // Estimate processing time
              // Stored in minutes. Round up
              if(!taskItem.editingDuration)
                taskItem.estimatedProcessingTime = Math.ceil(unitCount * taskType.handlingTimePerItem)

            } else {
              if(!taskItem.editingName)
                taskItem.name = this.generateDespatchTaskItemNameForOrders(taskItem, taskItem.orders, { addressLookupList: addressLookupList })

              if(!taskItem.editingDetails)
                taskItem.details = this.generateDespatchTaskDetailsForOrders(taskItem, taskItem.orders)
            }
          })

          return resolve(currentTaskItemList)
        } catch(err) {
          return reject(err)
        }
      })
    },

    calculateOrderItemListRequiredQuantity(orderItems) {
      if(!orderItems)
        return 0

      return orderItems.reduce((curValue, orderItem) => {
        if(!this.isOrderItemTrackableType(orderItem))
          return curValue
        
        return curValue + (orderItem.quantity - orderItem.despatchedQuantity)
      }, 0)
    },

    taskItemTotalScannedInQty(taskItem, scannedItemList) {
      if(!scannedItemList)
        return 0

      let scannedTaskItem = scannedItemList.find(scanTaskItem => scanTaskItem.id == taskItem.id)
      if(scannedTaskItem) {
        return scannedTaskItem.taskItemScannedItems.reduce((curValue, scannedItem) => {
          // Don't include scanned in quantity if not a trackable order item
          let orderItem = taskItem.orderItems.find(orderItem => orderItem.id == scannedItem.orderItemId)
          if(orderItem && !this.isOrderItemTrackableType(orderItem))
            return curValue

          return curValue + scannedItem.scannedInQuantity
        }, 0)
      }

      return 0
    },
    taskItemTotalScannedOutQty(taskItem, scannedItemList) {
      if(!scannedItemList)
        return 0

      let scannedTaskItem = scannedItemList.find(scanTaskItem => scanTaskItem.id == taskItem.id)
      if(scannedTaskItem) {
        return scannedTaskItem.taskItemScannedItems.reduce((curValue, scannedItem) => {
          // Don't include scanned out quantity if not a trackable order item
          let orderItem = taskItem.orderItems.find(orderItem => orderItem.id == scannedItem.orderItemId)
          if(orderItem && !this.isOrderItemTrackableType(orderItem))
            return curValue

          return curValue + scannedItem.scannedOutQuantity
        }, 0)
      }

      return 0
    },
    taskItemTotalRequiredQty(taskItem) {
      if(!taskItem || !taskItem.orderItems)
        return 0
      
      // Note: This will be zero after the task item is successfully completed & break the display of the loading info
      // So we used to store 'totalOrderItemRequiredQuantity' value of task item as well & get largest
      // Done during migration to new task system where old task items didn't have the value set
      var requiredQuantity = taskItem.orderItems.reduce((curValue, orderItem) => {
        if(!this.isOrderItemTrackableType(orderItem))
          return curValue
        
        return curValue + (orderItem.quantity - orderItem.despatchedQuantity)
      }, 0)

      return Math.max(taskItem.totalOrderItemRequiredQuantity || 0, requiredQuantity)
    },
    taskItemRequiresLoading(taskItem) {
      if(taskItem.type != TaskItemTypes.orderDespatch)
        return false

      // Go through order items. If any is a trackable type, then it can be loadable
      if(taskItem.orderItems) {
        return taskItem.orderItems.some(orderItem => this.isOrderItemTrackableType(orderItem))
      }

      return false
    }
  }
}